import { ReactNode } from 'react';
import Link from 'next/link';

import PageLink from '@/utils/PageLink';

import DocsCallout from '../DocsCallout';
import { DocsTable } from '../DocsCards';
import DocsCodeBlock from '../DocsCodeBlock';
import DocsSteps, { DocsStep } from '../DocsSteps';

import { CHATCO_MCP_SCOPE } from './connector-hosts';

/*
 * Content shared by every external-expert host guide. These render section
 * bodies only — each page owns its <section id> and <h2> so its table of
 * contents stays in its own hands.
 *
 * Every behaviour described here is taken from the deployed gateway
 * (apps/api/src/mcp-gateway) and its consent page (apps/web/src/pages/connect).
 * Update this file, not the individual guides, when that behaviour changes.
 */

/** The synthetic expert used in every example. Never substitute a real account's expert. */
export const EXAMPLE_EXPERT = 'HR Handbook';

interface ConnectorPrerequisitesProps {
  /** Host-specific requirements, rendered as extra list items. */
  children?: ReactNode;
}

export const ConnectorPrerequisites: React.FC<ConnectorPrerequisitesProps> = ({ children }) => (
  <ul>
    <li>
      <strong>Your own Chat.co account.</strong> You sign in yourself during setup. Never use
      another person&apos;s login.
    </li>
    <li>
      <strong>A workspace with at least one expert you can chat with</strong> in Chat.co, on an
      active plan. If someone else manages the account, they give you access to experts; see{' '}
      <Link href={PageLink.docsTeamRoles}>Team, roles &amp; seats</Link>.
    </li>
    <li>
      <strong>Message credits in that workspace</strong> for expert answers and standalone searches.
      Listing experts and opening sources are free.
    </li>
    <li>
      <strong>Optional: permission to search an expert&apos;s knowledge directly.</strong> You only
      need it for standalone search. Asking questions and opening an answer&apos;s citations do not
      require it.
    </li>
    {children}
  </ul>
);

/** Explains the Chat.co approval page and what the single OAuth scope does and does not allow. */
export const ConsentExplainer: React.FC = () => (
  <>
    <p>
      Every host sends you to the same Chat.co approval page. Sign in if asked, then check each part
      before you approve:
    </p>
    <DocsTable
      headers={['On the approval page', 'What it means']}
      rows={[
        [
          <strong key="a">Signed in as</strong>,
          'The Chat.co account the app will act for. If it is the wrong account, cancel, sign out of Chat.co and start again from the app.',
        ],
        [
          <strong key="w">Workspace and billing account</strong>,
          'The one workspace this connection uses. Its experts, permissions and message credits apply. To use a different workspace later, disconnect and connect again.',
        ],
        [
          <strong key="c">This application will be able to</strong>,
          'Ask your experts and receive answers, citations and supporting-document links. Search knowledge you are permitted to read and open the sources it returns. When you ask for one, an expert may also produce an assessment or other file, which the app receives as a download.',
        ],
        [
          <strong key="n">Cannot</strong>,
          'Manage or delete your chatbots. The connector has no tools for editing knowledge, settings, team members or billing.',
        ],
        [
          <strong key="d">Connection details</strong>,
          'The requesting app’s client ID and return address. Use them to confirm this is the app you started from.',
        ],
      ]}
    />
    <p>
      Choose <strong>Approve connection</strong> to finish, or <strong>Cancel</strong> to stop. The
      only permission requested is <code>{CHATCO_MCP_SCOPE}</code>. It means the app cannot change
      your chatbots, knowledge, settings or account. It does not make the connection free of side
      effects: asking an expert saves the conversation in Chat.co and uses message credits, and a
      standalone search uses credits too, exactly as in the Chat.co app. Your questions, and the
      answers, excerpts and files you are permitted to receive, pass to the AI app and its provider.
    </p>
    <DocsCallout variant="warning" title="No API keys, no shared logins">
      This connector signs in with OAuth only. Do not paste a Chat.co API key, password or anyone
      else&apos;s credentials into an AI app to connect it. If a setup screen asks for a Chat.co API
      key for this server, you are in the wrong flow.
    </DocsCallout>
  </>
);

interface FirstRunWalkthroughProps {
  /** Where to type the prompts, e.g. "a new Claude chat". */
  where: string;
}

/** Discovery → cited answer → follow-up → source → optional search, with the expected result of each. */
export const FirstRunWalkthrough: React.FC<FirstRunWalkthroughProps> = ({ where }) => (
  <>
    <p>
      Run these in {where}. <em>{EXAMPLE_EXPERT}</em> is a made-up example; use the name of one of
      your own experts.
    </p>
    <DocsSteps>
      <DocsStep title="Discover your experts">
        <DocsCodeBlock code="Which Chat.co experts can I ask?" language="prompt" />
        <p>
          <strong>Expect:</strong> the experts in the workspace you approved, with short
          descriptions, and whether you can search each one&apos;s documents directly. Free.
        </p>
      </DocsStep>
      <DocsStep title="Ask a question and request citations">
        <DocsCodeBlock
          code={`Ask the ${EXAMPLE_EXPERT} expert how many vacation days a new employee gets. Cite the sources.`}
          language="prompt"
        />
        <p>
          <strong>Expect:</strong> the expert&apos;s answer with citations that link to supporting
          documents. It is charged once, at the expert&apos;s normal Chat.co rate. Expert answers
          can take longer than an ordinary chat reply; while it waits, the app may check back on the
          same question several times, and you are still charged once. Answers do not include
          credit-cost notes; your usage appears in Chat.co.
        </p>
      </DocsStep>
      <DocsStep title="Follow up in the same conversation">
        <DocsCodeBlock code="Does that change after two years of service?" language="prompt" />
        <p>
          <strong>Expect:</strong> an answer that builds on the previous one, because the app
          continues the same expert conversation. If the reply ignores the earlier context, ask the
          app to continue the same Chat.co conversation.
        </p>
      </DocsStep>
      <DocsStep title="Open a source">
        <DocsCodeBlock code="Open the source for the first citation." language="prompt" />
        <p>
          <strong>Expect:</strong> the cited passage in the conversation, or a link to a Chat.co
          supporting-document page. Private documents ask you to sign in with the account that
          received the answer. Opening sources is free, even when credits have run out.
        </p>
      </DocsStep>
      <DocsStep title="Optional: search documents directly">
        <DocsCodeBlock
          code={`Search the ${EXAMPLE_EXPERT} expert's documents for "parental leave".`}
          language="prompt"
        />
        <p>
          <strong>Expect:</strong> a list of matching sources you can open. This works only for
          experts marked as directly searchable. Each completed search costs 0.5 message credit,
          even when nothing matches.
        </p>
      </DocsStep>
    </DocsSteps>
  </>
);

export const ConnectorCreditsTable: React.FC = () => (
  <>
    <DocsTable
      headers={['Activity', 'Charge']}
      rows={[
        ['Ask an expert (including follow-ups)', 'The expert’s normal Chat.co message rate'],
        ['Completed standalone document search', '0.5 message credit'],
        ['Retrieval an expert performs inside its answer', 'No additional charge'],
        ['List experts or open returned sources', 'Free'],
      ]}
    />
    <p>
      A search that completes with no matches still counts. Failed operations, inaccessible experts
      and retries of the same operation are not charged again. Your existing allocation applies;
      there is no checkout inside the connector. When credits run out, new paid requests stop, but
      you can still list experts and open sources you already received.
    </p>
  </>
);

interface ConnectorErrorsProps {
  /** Host-specific rows shown before the shared ones. */
  hostRows?: ReactNode[][];
}

export const ConnectorErrors: React.FC<ConnectorErrorsProps> = ({ hostRows = [] }) => (
  <DocsTable
    headers={['What you see', 'Cause and fix']}
    rows={[
      ...hostRows,
      [
        '“This connection request expired or could not be verified.”',
        'The sign-in link was stale or already used. Start the connection again from the app.',
      ],
      [
        '“No accessible workspace” on the approval page',
        'This login has no workspace with expert access. Sign in with the right account, or ask the account owner for access.',
      ],
      [
        'An expert is missing from the list',
        'You chose a different workspace, or your access or licence changed. Check the expert in Chat.co, then disconnect and reconnect with the right workspace.',
      ],
      [
        '“Expert unavailable.”',
        'The expert is not available to this connection. Ask the app to list your experts again and pick one from that list.',
      ],
      [
        '“This expert has insufficient message credits…”',
        'The allocation is used up. Listing experts and opening sources still work. Add credits through your usual Chat.co process.',
      ],
      [
        '“The selected account does not have an active subscription.”',
        'The workspace’s Chat.co plan is not active. The account owner can check billing in Chat.co.',
      ],
      [
        '“The expert took too long. This operation was not charged.”',
        'Try again, or split a very broad question into smaller ones.',
      ],
      [
        'Search returns nothing, or asks which expert should pay',
        'Name one expert in the request. Direct search works only for experts marked as searchable.',
      ],
      [
        '“This document is unavailable or you no longer have access.”',
        'Sign in to Chat.co with the account that received the answer. Removed access or a deleted source cannot be opened.',
      ],
    ]}
  />
);

interface ChatcoDisconnectProps {
  /** How to remove Chat.co on the host side. */
  children: ReactNode;
}

export const ChatcoDisconnect: React.FC<ChatcoDisconnectProps> = ({ children }) => (
  <>
    <DocsSteps>
      <DocsStep title="Revoke access in Chat.co">
        <p>
          Open{' '}
          <Link href="/connect" className="font-medium text-primary-700 underline">
            Connected applications
          </Link>{' '}
          in Chat.co, find the app and workspace, and choose <strong>Disconnect</strong>. The app
          loses access to the connector from its next request.
        </p>
      </DocsStep>
      <DocsStep title="Remove the server from the app">{children}</DocsStep>
    </DocsSteps>
    <p>
      Disconnecting stops future access. It cannot remove answers or excerpts the app already
      received; that app&apos;s own terms and privacy policy govern them. To reconnect, start again
      from the app.
    </p>
  </>
);

/** Section closing every host guide: back to the overview plus the vendor's own documentation. */
export const ConnectorReferences: React.FC<{ vendorLinks: { label: string; href: string }[] }> = ({
  vendorLinks,
}) => (
  <ul>
    <li>
      <Link href={PageLink.docsExternalExperts}>Chat.co experts in your AI apps</Link> &mdash;
      overview, status of every host, credits and privacy
    </li>
    <li>
      <Link href="/connect">Connected applications</Link> &mdash; review or disconnect
    </li>
    {vendorLinks.map((link) => (
      <li key={link.href}>
        <a href={link.href} target="_blank" rel="noopener noreferrer">
          {link.label}
        </a>
      </li>
    ))}
    <li>
      Support: <a href="mailto:support@chat.co">support@chat.co</a>
    </li>
  </ul>
);
